/**
 * Standardized error messages for consistent user experience
 */

/** Shown for generic RBAC/forbidden responses (toast + fallback when detail is absent) */
export const PERMISSION_CONTACT_ADMINISTRATOR =
  "You don't have permission to perform this action, please contact your administrator."

export const ERROR_MESSAGES = {
  // Generic errors
  GENERIC: 'An unexpected error occurred',
  NETWORK_ERROR: 'Unable to reach our servers. Please check your internet connection.',
  SOMETHING_WENT_WRONG: 'Something went wrong',

  // API operation errors
  FAILED_TO_LOAD: (resource: string) => `Failed to load ${resource}`,
  FAILED_TO_CREATE: (resource: string) => `Failed to create ${resource}`,
  FAILED_TO_UPDATE: (resource: string) => `Failed to update ${resource}`,
  FAILED_TO_DELETE: (resource: string) => `Failed to delete ${resource}`,
  FAILED_TO_SAVE: (resource: string) => `Failed to save ${resource}`,

  // Specific resource errors
  FAILED_TO_LOAD_PROJECTS: 'Failed to load projects',
  FAILED_TO_LOAD_MODELS: 'Failed to load models',
  FAILED_TO_LOAD_AGENTS: 'Failed to load agents',
  FAILED_TO_LOAD_TOOLS: 'Failed to load tools',
  FAILED_TO_LOAD_PROMPTS: 'Failed to load prompts',
  FAILED_TO_LOAD_KNOWLEDGE: 'Failed to load knowledge connectors',
  FAILED_TO_LOAD_ROLES: 'Failed to load roles',
  FAILED_TO_LOAD_SESSIONS: 'Failed to load chat sessions',
  FAILED_TO_LOAD_DATA: 'Failed to load data',
  PAYLOAD_TOO_LARGE: 'Payload too large. Maximum allowed size is 1MB.',

  // Authentication errors
  FAILED_TO_LOGIN: 'Failed to log in. Please check your credentials.',
  FAILED_TO_LOAD_USER: 'Failed to load user information',
  FAILED_TO_STORE_TOKEN: 'Failed to store authentication token',
  AUTHENTICATION_FAILED: 'Your session has expired. Please log in again.',
  AUTHENTICATION_REQUIRED: 'You must be logged in to perform this action.',
  TOKEN_EXPIRED: 'Your session has expired. Please log in again.',

  // Theme errors
  FAILED_TO_LOAD_THEME: 'Failed to load theme',
  FAILED_TO_SAVE_THEME: 'Failed to save theme preference',

  // Chat errors
  FAILED_TO_CREATE_SESSION: 'Failed to create chat session',
  FAILED_TO_LOAD_SESSION: 'Failed to load chat session',
  FAILED_TO_SEND_MESSAGE: 'Failed to send message',
  FAILED_TO_START_RECORDING: 'Failed to start recording. Please check microphone permissions.',

  // Model errors
  MODEL_NOT_FOUND: 'Model not found',
  MODEL_NOT_SELECTED: 'Please select a model',
  FAILED_TO_TEST_CONNECTION: 'Failed to test model connection',

  // Permission errors (RBAC)
  PERMISSION_DENIED: 'You do not have permission to perform this action',
  PERMISSION_DENIED_UPDATE: (resource: string) =>
    `You don't have permission to update this ${resource}. Only the owner or an administrator can modify it.`,
  PERMISSION_DENIED_DELETE: (resource: string) =>
    `You don't have permission to delete this ${resource}. Only the owner or an administrator can delete it.`,
  PERMISSION_DENIED_VIEW: (resource: string) =>
    `You don't have permission to view this ${resource}.`,
  PERMISSION_DENIED_CREATE: (resource: string) =>
    `You don't have permission to create ${resource}.`,
  PERMISSION_CONTACT_ADMINISTRATOR,
  FORBIDDEN_ERROR: PERMISSION_CONTACT_ADMINISTRATOR,

  // Validation errors
  INVALID_INPUT: 'Invalid input provided',
  REQUIRED_FIELD: (field: string) => `${field} is required`,

  /** Connector register-tables: selected knowledge is already in the knowledge base */
  TABLES_ALREADY_REGISTERED:
    'This knowledge is already registered. It already appears in your knowledge base—deselect it or choose tables that are not yet added.',

  /** Registry request decide raced with another tab/user */
  REQUEST_ALREADY_APPROVED: 'This request was already approved.',
  REQUEST_ALREADY_REJECTED: 'This request was already rejected.',
  REQUEST_NO_LONGER_PENDING: 'This request is no longer pending.',

  // Connector-specific errors
  CONNECTOR_TIMEOUT:
    'Connection timed out. Check the host, port, and network access, then retry.',
  CONNECTOR_AUTH_MISMATCH:
    'Signed-in account does not match the connector identity. Re-authenticate or update the role mapping.',
  CONNECTOR_DB_NOT_FOUND:
    'Database not found or not accessible. Verify the database name in connector settings.',
  CONNECTOR_RELATION_NOT_FOUND:
    'Table or dataset not found — the schema may have changed. Use "Refresh metadata" to update.',
  CONNECTOR_NO_ROUTE:
    'Cannot reach the database host. Check the hostname, port, and network path.',
  CONNECTOR_BUSY:
    'System is temporarily busy. Please retry in a few seconds.',
  CONNECTOR_AUTH_MODE_REQUIRED:
    'Select an authentication mode (Service Principal or User Delegation) before connecting.',
  CONNECTOR_PASSWORD_REQUIRED:
    'Password is required for this connector. Enter credentials and try again.',
  CONNECTOR_CATALOG_NOT_FOUND:
    'Catalog not found. Verify the catalog name and ensure your account has access.',
  CONNECTOR_INTROSPECTION_FAILED:
    'Could not introspect connector. Check connection settings and try again.',
  CROSS_TENANT_DATA_PREVIEW:
    "Data unavailable. Switch to this table's tenant to view the preview.",
} as const

/**
 * Chat-specific error messages mapped by HTTP status code.
 * These messages are shown directly in the chat history instead of toasts,
 * providing a more intuitive user experience.
 */
export const CHAT_ERROR_MESSAGES: Record<
  number | string,
  { title: string; message: string; action?: string }
> = {
  // 4xx Client Errors
  400: {
    title: 'Bad Request',
    message:
      'The request was invalid. This may be due to an unsupported file format or malformed input.',
    action: 'Please check your input and try again.',
  },
  402: {
    title: 'Payment Required',
    message: 'This model requires additional credits or a paid plan.',
    action: 'Please check your subscription status.',
  },
  403: {
    title: 'Access Denied',
    message: "You don't have permission to chat with this agent or use this feature.",
    action: 'Ask a project admin to add you as a member, or request Agent Store access for UAT/PROD.',
  },
  404: {
    title: 'Model Not Found',
    message:
      'This model is no longer available. It may have been deleted or you no longer have access.',
    action: 'Select a different model to continue.',
  },
  session_not_found: {
    title: 'Chat session expired',
    message:
      'This chat session is no longer available. Your message can be retried on a new session.',
    action: 'Start a new chat',
  },
  408: {
    title: 'Request Timeout',
    message: 'The request timed out. The model took too long to respond.',
    action: 'Please try again with a shorter prompt.',
  },
  413: {
    title: 'Payload Too Large',
    message: 'The uploaded file or message is too large.',
    action: 'Please reduce the file size or shorten your message and try again.',
  },
  415: {
    title: 'Unsupported Media Type',
    message: 'This file type is not supported by the selected model.',
    action: 'Please try a different file format.',
  },
  422: {
    title: 'Unprocessable Content',
    message: 'The content could not be processed. The model may not support this type of input.',
    action: 'Try reformatting your message or using different content.',
  },
  429: {
    title: 'Rate Limit Exceeded',
    message: 'Too many requests. The model is temporarily rate-limited.',
    action: 'Please wait a moment before sending another message.',
  },
  410: {
    title: 'Agent Unavailable',
    message: 'This agent is no longer available. It may have been deleted.',
    action: 'Return to Agent Studio to choose another agent.',
  },
  AGENT_UNAVAILABLE: {
    title: 'Agent Unavailable',
    message: 'This agent is no longer available. It may have been deleted.',
    action: 'Return to Agent Studio to choose another agent.',
  },
  AGENT_ARCHIVED: {
    title: 'Agent Archived',
    message: 'This agent is archived and cannot be used for chat. Restore it to draft before chatting.',
    action: 'Restore the agent in Agent Studio, then try again.',
  },

  // 5xx Server Errors
  500: {
    title: 'Server Error',
    message: 'An unexpected server error occurred.',
    action: 'Please try again in a few moments.',
  },
  502: {
    title: 'Service Unavailable',
    message: 'The model service is temporarily unavailable.',
    action: 'Please try again shortly.',
  },
  503: {
    title: 'Service Overloaded',
    message: 'The model is currently overloaded or under maintenance.',
    action: 'Please try again later.',
  },
  504: {
    title: 'Gateway Timeout',
    message: 'The model service timed out. The request may have been too complex.',
    action: 'Please try a simpler query.',
  },

  // Network/Connection Errors
  network: {
    title: 'Connection Failed',
    message: 'Unable to reach the server. Please check your internet connection.',
    action: 'Try again when your connection is restored.',
  },

  graph_visit_limit: {
    title: 'Graph stopped',
    message:
      'The graph hit its visit limit and stopped. This usually means a cycle on the canvas.',
    action: 'Open the canvas and point loop-back edges forward, or enable cycles only on a supervisor that should loop.',
  },

  // Generic fallback
  unknown: {
    title: 'Error',
    message: 'An unexpected error occurred while processing your request.',
    action: 'Please try again.',
  },
} as const
